import { Combobox } from '@headlessui/react'
import SearchIcon from '@heroicons/react/24/solid/MagnifyingGlassIcon'
import {
  ChangeEventHandler,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '~/utils/cn'

export function SearchableInputField<T>({
  options,
  value,
  newValueGenerator,
  keyGetter,
  labelGetter,
  labelRenderer,
  onChange,
  id,
  placeholder,
  inputClassName,
  inputContainerClassName,
  nullable,
  inputFieldRef,
  forceSelectOnBlur,
}: {
  options: T[]
  newValueGenerator?: (value: string) => T
  keyGetter: (selection: T) => string | number
  labelGetter: (selection: T) => string
  labelRenderer?: (selection: T) => ReactNode
  value: T
  onChange: (value: T) => void
  placeholder?: string
  id?: string
  inputClassName?: string
  inputContainerClassName?: string
  nullable?: boolean
  inputFieldRef?: Ref<HTMLInputElement>
  forceSelectOnBlur?: boolean
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const highlighted = useRef<T | null>(null)
  const handleSelectionChange = useCallback(
    (value: T) => {
      onChange(value)
    },
    [onChange]
  )
  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    setSearchQuery(evt.target.value)
  }

  const optionsFiltered = useMemo(() => {
    const trimmedSearchQuery = searchQuery.trim()
    if (trimmedSearchQuery.length > 0) {
      return options.filter((option) =>
        labelGetter(option)
          .toLowerCase()
          .includes(trimmedSearchQuery.toLowerCase())
      )
    }
    return options
  }, [options, searchQuery])

  const getExactMatchOptionByLabel = useCallback(
    (label: string) =>
      optionsFiltered.find(
        (option) =>
          labelGetter(option).trim().toLowerCase() ===
          label.trim().toLowerCase()
      ),
    [optionsFiltered]
  )

  const exactMatchOption = useMemo(
    () => getExactMatchOptionByLabel(searchQuery),
    [optionsFiltered, searchQuery]
  )

  const renderOption = useCallback((option: T, searchQuery?: string) => {
    const optionKey = keyGetter(option)
    const optionLabel = labelGetter(option)
    const highlightedRefKey = highlighted.current
      ? keyGetter(highlighted.current)
      : ''
    return (
      <Combobox.Option
        key={optionKey}
        value={option}
        className={({ active }) => {
          // Use the active state here to set highlighted value ref
          // If active set as current.
          // If not then check if the current ref value equals this
          // and clear if they match since it isn't active anymore
          if (active) {
            highlighted.current = option
          } else if (optionKey === highlightedRefKey) {
            highlighted.current = null
          }
          return `relative cursor-default select-none py-2 pl-10 pr-4 ${
            active ? 'bg-primary-500 text-white' : 'text-gray-900'
          }`
        }}
      >
        {({ selected, active }) =>
          searchQuery ? (
            <>
              Create <span className="font-bold">{`"${searchQuery}"`}</span>
            </>
          ) : labelRenderer ? (
            <>{labelRenderer(option)}</>
          ) : (
            <span
              className={`block truncate ${
                selected ? 'font-medium' : 'font-normal'
              }`}
            >
              {optionLabel}
            </span>
          )
        }
      </Combobox.Option>
    )
  }, [])

  return (
    <Combobox
      value={value}
      onChange={handleSelectionChange}
      // @ts-ignore
      nullable={nullable}
    >
      <div className="relative mt-1">
        <div
          className={cn(
            'relative w-full cursor-default',
            inputContainerClassName
          )}
        >
          <Combobox.Input
            ref={inputFieldRef}
            id={id}
            className={cn(
              'w-full p-3.5 border border-[#D6D9DC] rounded-md resize-none text-[#414141] placeholder:text-[#707070] text-sm',
              inputClassName
            )}
            displayValue={(value: T | null) =>
              value ? labelGetter(value) : ''
            }
            onChange={handleSearchChange}
            placeholder={placeholder}
            autoComplete="off"
            onBlur={() => {
              // If the forceSelectOnBlur is true
              // we would want to select
              //    the highlighted option if exists
              //    Or the exact match option against the search query if exists
              //    Or create a new value against the search query
              if (forceSelectOnBlur) {
                if (highlighted.current) {
                  onChange(highlighted.current)
                  return
                }
                if (!searchQuery) {
                  return
                }
                const exactMatchOption = getExactMatchOptionByLabel(searchQuery)
                if (exactMatchOption) {
                  onChange(exactMatchOption)
                  return
                }
                if (newValueGenerator) {
                  onChange(newValueGenerator(searchQuery))
                }
              }
            }}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <SearchIcon className="text-gray-400 w-4 inline-block" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
          {searchQuery.length > 0 &&
            !exactMatchOption &&
            newValueGenerator &&
            renderOption(newValueGenerator(searchQuery), searchQuery)}
          {optionsFiltered.length === 0 && searchQuery !== '' ? (
            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
              Nothing found.
            </div>
          ) : (
            optionsFiltered.map((option) => renderOption(option))
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
